import { useTranslation } from 'react-i18next';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '../../ui/button';
import NumberInput from '../../ui/number-input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../ui/select';
import SectionHeading from './SectionHeading';
import { FormField, FormItem, FormControl, FormMessage } from '../../ui/form';
import { MachineFormValues } from '../types';
import { SAR_OPERATING_MODES } from '../../../models/MRIMachineModels';

const SAR_FIELDS = ['level', 'wholeBody', 'head'] as const;
const SAR_VALUE_FIELDS = ['wholeBody', 'head'] as const;

export default function MaxSARSection() {
    const { t } = useTranslation();
    const form = useFormContext<MachineFormValues>();
    const maxSARFields = useFieldArray({
        control: form.control,
        name: 'MaxSAR',
    });
    const usedLevels = form
        .watch('MaxSAR')
        .map((sar) => sar.level)
        .filter((level) => level !== '');
    const nextMode = SAR_OPERATING_MODES.find(
        (mode) => !usedLevels.includes(mode.level)
    );

    return (
        <div className="space-y-3">
            <SectionHeading
                title={t('roomOverview.mriMachineForm.form.maxSAR.label')}
                description={t(
                    'roomOverview.mriMachineForm.form.maxSAR.description'
                )}
            />
            {maxSARFields.fields.length === 0 ? (
                <p className="text-sm py-4 text-center">
                    {t('roomOverview.mriMachineForm.form.maxSAR.emptyState')}
                </p>
            ) : (
                <>
                    <div className="grid grid-cols-[2rem_minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)_2.5rem] gap-3 text-xs font-medium">
                        <span>#</span>
                        {SAR_FIELDS.map((name) => (
                            <span key={name}>
                                {t(
                                    `roomOverview.mriMachineForm.form.maxSAR.${name}`
                                )}
                            </span>
                        ))}
                        <span />
                    </div>

                    {maxSARFields.fields.map((field, index) => (
                        <div
                            key={field.id}
                            className="grid grid-cols-[2rem_minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)_2.5rem] gap-3 items-start"
                        >
                            <span className="flex h-10 items-center text-xs tabular-nums">
                                {index + 1}
                            </span>
                            <FormField
                                control={form.control}
                                name={`MaxSAR.${index}.level`}
                                rules={{
                                    validate: (level, values) => {
                                        if (
                                            !SAR_OPERATING_MODES.some(
                                                (mode) => mode.level === level
                                            )
                                        )
                                            return 'Select an operating mode';
                                        const uses = values.MaxSAR.filter(
                                            (sar) => sar.level === level
                                        ).length;
                                        return (
                                            uses === 1 ||
                                            'Operating mode already used'
                                        );
                                    },
                                }}
                                render={({ field }) => (
                                    <FormItem>
                                        <Select
                                            value={String(field.value)}
                                            onValueChange={(val) =>
                                                field.onChange(Number(val))
                                            }
                                        >
                                            <FormControl>
                                                <SelectTrigger className="h-10 text-xs">
                                                    <SelectValue />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                {SAR_OPERATING_MODES.map(
                                                    (mode) => (
                                                        <SelectItem
                                                            key={mode.level}
                                                            value={String(
                                                                mode.level
                                                            )}
                                                            disabled={
                                                                mode.level !==
                                                                    field.value &&
                                                                usedLevels.includes(
                                                                    mode.level
                                                                )
                                                            }
                                                        >
                                                            {mode.label}
                                                        </SelectItem>
                                                    )
                                                )}
                                            </SelectContent>
                                        </Select>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            {SAR_VALUE_FIELDS.map((name) => (
                                <FormField
                                    key={name}
                                    control={form.control}
                                    name={`MaxSAR.${index}.${name}`}
                                    rules={{
                                        required: 'SAR value is required',
                                    }}
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormControl>
                                                <NumberInput {...field} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            ))}
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="mt-1 h-8 w-8 hover:text-destructive"
                                onClick={() => maxSARFields.remove(index)}
                                aria-label={t(
                                    'roomOverview.mriMachineForm.buttons.delete'
                                )}
                            >
                                <Trash2
                                    className="h-4 w-4"
                                    aria-hidden="true"
                                />
                            </Button>
                        </div>
                    ))}
                </>
            )}

            <div className="grid grid-cols-[2rem_minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)_2.5rem] gap-3">
                <div className="col-start-5 flex justify-end">
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={!nextMode}
                        onClick={() =>
                            nextMode &&
                            maxSARFields.append({
                                id: uuidv4(),
                                level: nextMode.level,
                                wholeBody: 0,
                                head: 0,
                            })
                        }
                    >
                        <Plus className="h-4 w-4 mr-1" aria-hidden="true" />
                        {t('roomOverview.mriMachineForm.form.maxSAR.addButton')}
                    </Button>
                </div>
            </div>
        </div>
    );
}
